import React from 'react';
import { TallBuildingError } from '@/services/fsiReport';
import { AlertTriangle, XCircle, CheckCircle, ArrowRight, Building, MapPin } from 'lucide-react';
import { Button } from './ui/button';

interface TallBuildingErrorDisplayProps {
  error: TallBuildingError;
  onSelectAlternative: (floors: number) => void;
  onClose: () => void;
}

const TallBuildingErrorDisplay: React.FC<TallBuildingErrorDisplayProps> = ({
  error,
  onSelectAlternative,
  onClose,
}) => {
  const getAlternativeFloors = (option: any): number => {
    if (option.description.includes('4 floors')) return 4;
    if (option.description.includes('23 floors')) return 23;
    return error.restrictions.max_floors_allowed;
  };

  return (
    <div className="space-y-6">
      {/* Error Header */}
      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <XCircle className="w-7 h-7 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-red-900 mb-2">Tall Building Not Permitted</h3>
            <p className="text-red-700 text-lg mb-4">{error.message}</p>
            <div className="bg-white rounded-lg p-4 border border-red-200">
              <p className="text-sm text-gray-700">
                <strong>Requested Floors:</strong> {error.requested_floors} floors
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Maximum Allowed:</strong> {error.restrictions.max_floors_allowed} floors
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Restrictions Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          Height & Zone Restrictions
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Primary Road Width</p>
            <p className="text-xl font-bold text-gray-800">
              {error.restrictions.primary_road_width_m} meters
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Max Building Height</p>
            <p className="text-xl font-bold text-gray-800">
              {error.restrictions.max_building_height_m} meters
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Max Floors Allowed</p>
            <p className="text-xl font-bold text-gray-800">
              {error.restrictions.max_floors_allowed} floors
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-1">Zone Restriction</p>
            <p className="text-xl font-bold text-gray-800">
              {error.restrictions.zone_restriction} floors max
            </p>
          </div>
        </div>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Calculation Basis:</strong> {error.restrictions.calculation_basis}
          </p>
        </div>
      </div>

      {/* Tall Building Requirements */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-purple-600" />
          Tall Building Requirements (&gt;23 floors)
        </h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-700">Minimum Road Width Required:</span>
            <span className="font-bold text-gray-800">
              {error.tall_building_requirements.minimum_road_width_required}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-700">Search Radius:</span>
            <span className="font-bold text-gray-800">
              {error.tall_building_requirements.search_radius}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-700">Nearby Roads Checked:</span>
            <span className="font-bold text-gray-800">
              {error.tall_building_requirements.nearby_roads_checked}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-200">
            <span className="text-gray-700">Widest Road Found:</span>
            <span className="font-bold text-red-600">
              {error.tall_building_requirements.widest_road_found_m}m 
              (Gap: {error.tall_building_requirements.gap_to_requirement_m}m)
            </span>
          </div>
        </div>
      </div>

      {/* Alternatives */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          Alternative Development Options
        </h4>
        <p className="text-gray-600 mb-4">
          Choose one of these alternatives to proceed with your development:
        </p>
        
        <div className="space-y-4">
          {/* Option 1 */}
          <div className="border-2 border-gray-200 rounded-lg p-5 hover:border-green-500 hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-3">
              <h5 className="font-bold text-gray-800 text-lg">Option 1: Maximum Allowed</h5>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                Recommended
              </span>
            </div>
            <p className="text-gray-700 mb-4">{error.alternatives.option_1.description}</p>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-gray-600">Max Floors:</p>
                <p className="font-bold text-gray-800">{error.alternatives.option_1.max_floors}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-gray-600">Approvals:</p>
                <p className="font-medium text-gray-800">{error.alternatives.option_1.approvals_required}</p>
              </div>
            </div>
            <Button
              onClick={() => onSelectAlternative(getAlternativeFloors(error.alternatives.option_1))}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              Select {error.alternatives.option_1.max_floors} Floors
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Option 2 */}
          <div className="border-2 border-gray-200 rounded-lg p-5 hover:border-blue-500 hover:shadow-md transition-all">
            <h5 className="font-bold text-gray-800 text-lg mb-3">Option 2: Standard High-Rise</h5>
            <p className="text-gray-700 mb-4">{error.alternatives.option_2.description}</p>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-gray-600">Max Floors:</p>
                <p className="font-bold text-gray-800">{error.alternatives.option_2.max_floors}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-gray-600">Approvals:</p>
                <p className="font-medium text-gray-800">{error.alternatives.option_2.approvals_required}</p>
              </div>
            </div>
            <Button
              onClick={() => onSelectAlternative(getAlternativeFloors(error.alternatives.option_2))}
              variant="outline"
              className="w-full"
            >
              Select {error.alternatives.option_2.max_floors} Floors
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Option 3 */}
          <div className="border-2 border-gray-200 rounded-lg p-5 hover:border-purple-500 hover:shadow-md transition-all">
            <h5 className="font-bold text-gray-800 text-lg mb-3">Option 3: Different Plot</h5>
            <p className="text-gray-700 mb-4">{error.alternatives.option_3.description}</p>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <MapPin className="w-4 h-4 inline mr-1" />
                <strong>Requirement:</strong> {error.alternatives.option_3.minimum_road_width_needed}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
        <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-600" />
          Our Recommendation
        </h4>
        <p className="text-gray-700">{error.recommendation}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={onClose}
          variant="outline"
          className="flex-1"
        >
          Go Back
        </Button>
      </div>
    </div>
  );
};

export default TallBuildingErrorDisplay;
